import type { KudoContentObjectList, LabelInValue, UploadedFile } from 'models';
import { useCallback } from 'react';
import { usePermissions } from 'permissions';
import { Button, Col, Form, Icon, RichTextInput, Row } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { CommunitySelectWithUserProfilePhoto } from 'features/communities/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import type { CommunityLabelInValue } from 'features/communities/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useCommunityMentions } from 'features/communities/hooks';
import { AttachmentInput, FileAttachments } from '../comment-form';
import { ExpressingKudo } from '../expressing-kudo';
import { PinnedPostToggle } from '../pinned-post-toggle';
import {
  useAttachmentsRules,
  useCommunityRules,
  useGetInitialValues,
  useHtmlContentRules,
  useRecipientRules,
} from './hooks';

export type KudoFormValues = {
  attachments: { files: UploadedFile[]; loading: boolean };
  community?: CommunityLabelInValue;
  htmlContent: string;
  pinned: boolean;
  textContent: string;
  user: LabelInValue | null;
};

type P = {
  isLoading?: boolean;
  kudoContentObject?: KudoContentObjectList;
  mode: 'create' | 'edit';
  onSubmit: (values: KudoFormValues) => void;
};

const KudoForm = ({ isLoading, kudoContentObject, mode, onSubmit }: P) => {
  const [form] = Form.useForm<KudoFormValues>();
  const formInitialValues = useGetInitialValues(kudoContentObject);
  const { canManageSelectedCommunity } = usePermissions();
  const attachments = Form.useWatch<KudoFormValues['attachments'] | undefined>('attachments', form) ?? {
    files: [],
    loading: false,
  };
  const community = Form.useWatch<KudoFormValues['community']>('community', form);
  const communityRules = useCommunityRules();
  const recipientRules = useRecipientRules();
  const htmlContentRules = useHtmlContentRules();
  const attachmentsRules = useAttachmentsRules();
  const getCommunityId = useCallback(() => form.getFieldsValue().community?.value, [form]);
  const handleOnRequestMentions = useCommunityMentions(getCommunityId);

  return (
    <Form<KudoFormValues> form={form} initialValues={formInitialValues} onFinish={onSubmit}>
      <Row>
        <Col span={24}>
          <Form.Item<KudoFormValues> name="community" rules={communityRules}>
            <CommunitySelectWithUserProfilePhoto />
          </Form.Item>
        </Col>

        <Col span={24}>
          <RichTextInput
            html={{
              className: 'post-form__editor',
              dependencies: ['attachments'],
              isFocusedInitially: true,
              label: 'Kudos',
              mentions: handleOnRequestMentions,
              name: 'htmlContent',
              noOptional: true,
              placeholder: 'Write content...',
              rules: htmlContentRules,
            }}
            text={{ name: 'textContent' }}
          />
        </Col>

        <Col className="post-form__attachments" span={24}>
          {attachments.files.length > 0 ? (
            <FileAttachments
              attachments={attachments.files}
              onClear={() => {
                form.setFieldsValue({ attachments: { files: [], loading: attachments.loading } });
              }}
              onRemove={(attachment) => {
                form.setFieldsValue({
                  attachments: {
                    files: attachments.files.filter((file) => file !== attachment),
                    loading: attachments.loading,
                  },
                });
              }}
            />
          ) : null}
        </Col>

        <Col span={24}>
          <Form.Item<KudoFormValues>
            label="Expressing Kudos to:"
            layout="horizontal"
            name="user"
            noOptional
            rules={recipientRules}
          >
            <ExpressingKudo communityIds={community ? [community.value] : []} disabled={mode === 'edit'} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Row align="middle" justify="space-between">
            <Col>
              <Form.Item<KudoFormValues> name="attachments" noStyle rules={attachmentsRules}>
                <AttachmentInput />
              </Form.Item>
            </Col>
            <Col>
              <Row align="middle" gutter={['lg', 'lg']}>
                {canManageSelectedCommunity({ community }) ? (
                  <Col>
                    <Form.Item<KudoFormValues> name="pinned" noStyle>
                      <PinnedPostToggle />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col>
                  <Button
                    htmlType="submit"
                    icon={mode === 'create' ? <Icon icon="medal-line" /> : undefined}
                    loading={isLoading}
                    type="primary"
                  >
                    {mode === 'create' ? 'Give Kudos' : 'Save'}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default KudoForm;
